import { html, CSSResultGroup, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import componentStyles from './tufts-video-lite.css.lit';
import { OutlineElement } from '../../base/outline-element/outline-element';

@customElement('tufts-video-lite')
export class TuftsVideoLite extends OutlineElement {
  static styles: CSSResultGroup = [componentStyles];

  @property({ type: String, reflect: true, attribute: 'video-link' })
  videoLink: string;

  @property({ type: String })
  thumbnailUrl: string;

  platform: string;
  videoId: string;

  async updated(changedProperties: Map<string, unknown>) {
    if (changedProperties.has('videoLink')) {
      await this.identifyProvider();
    }
  }

  async identifyProvider() {
    const youtubeRegex =
      /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?/ ]{11})/i;
    const vimeoRegex = /(?:vimeo)\.com.*(?:videos|video|channels|)\/([\d]+)/i;

    const youtubeMatch = this.videoLink.match(youtubeRegex);
    const vimeoMatch = this.videoLink.match(vimeoRegex);

    if (youtubeMatch && youtubeMatch[1]) {
      this.platform = 'youtube';
      this.videoId = youtubeMatch[1];
      this.thumbnailUrl = `https://i.ytimg.com/vi/${this.videoId}/maxresdefault.jpg`;
    } else if (vimeoMatch && vimeoMatch[1]) {
      this.platform = 'vimeo';
      this.videoId = vimeoMatch[1];
      this.thumbnailUrl = await this.fetchVimeoThumbnail(this.videoId);
      // By default, vimeo thumbnail URL comes with `-d_640` at the end.
      // We can retrieve the highest quality of thumbnail by
      // finding '-d_' and any characters afterwards, and convert it to `-d`
      // if '-d_' pattern is not found, thumbnailUrl remains the same
      this.thumbnailUrl = this.thumbnailUrl.replace(/-d_.*$/, '-d');
    }
  }

  async fetchVimeoThumbnail(videoId: string): Promise<string> {
    const response = await fetch(
      `https://vimeo.com/api/v2/video/${videoId}.json`
    );
    const data = await response.json();
    return data[0].thumbnail_large;
  }

  videoThumbnail(): TemplateResult | null {
    const slot = this.querySelector('[slot="video-image"]');
    if (slot) {
      const slotCopy = slot.cloneNode(true);
      return html`${slotCopy}`;
    } else if (this.thumbnailUrl) {
      return html`<img
        src="${this.thumbnailUrl}"
        @load=${(e: Event) => this.handleYoutubeThumbnailLoad(e)}
        alt="Video thumbnail"
      />`;
    }
    return null;
  }
  handleYoutubeThumbnailLoad(e: Event) {
    // YouTube returns a default thumbnail image of size 120x90 when the high-resolution thumbnail is not available
    // So, if the size of the loaded image is 120x90, we assume that the high-resolution thumbnail was not available
    if (this.platform !== 'youtube') {
      return;
    }
    const img = e.target as HTMLImageElement;
    if (img.naturalWidth === 120 && img.naturalHeight === 90) {
      // Construct the URL for the standard resolution thumbnail
      const sdUrl = `https://i.ytimg.com/vi/${this.videoId}/sddefault.jpg`;
      // Update the thumbnailUrl property to the standard resolution URL
      // This will trigger a re-render due to the property change
      this.thumbnailUrl = sdUrl;
    }
  }

  loadVideo() {
    const video = this.shadowRoot?.querySelector('iframe') as HTMLIFrameElement;
    if (video.dataset.src) {
      video.src = video.dataset.src;
    }
  }

  videoIframe(): TemplateResult {
    let iframeSrc = '';
    if (this.platform === 'youtube') {
      iframeSrc = `https://www.youtube.com/embed/${this.videoId}?autoplay=1`;
    } else if (this.platform === 'vimeo') {
      iframeSrc = `https://player.vimeo.com/video/${this.videoId}?autoplay=1`;
    }
    return html`<iframe
      allow="autoplay"
      src=""
      data-src="${iframeSrc}"
    ></iframe>`;
  }

  videoSummaryTemplate(): TemplateResult | null {
    const slot = this.querySelector('[slot="video-summary"]');
    if (slot) {
      const slotCopy = slot.cloneNode(true);
      return html`${slotCopy}`;
    }
    return null;
  }

  playIconTemplate(): TemplateResult | null {
    return html`
      <svg xmlns="http://www.w3.org/2000/svg" width="66" height="66">
        <path
          class="circle"
          d="M33 65c17.673 0 32-14.327 32-32C65 15.327 50.673 1 33 1 15.327 1 1 15.327 1 33c0 17.673 14.327 32 32 32Z"
        />
        <path
          class="play"
          fill-rule="evenodd"
          d="M44 32.5 26 20.81V44.19l18-11.69Zm-3.672 0L28 24.493v16.015L40.328 32.5Z"
          clip-rule="evenodd"
        />
      </svg>
    `;
  }

  render() {
    return html`
      <div class="video-wrapper">
        ${this.videoThumbnail()} ${this.videoIframe()}
        <button @click="${this.loadVideo}" aria-label="Play video">
          ${this.playIconTemplate()}
        </button>
      </div>
      ${this.videoSummaryTemplate()}
    `;
  }
}
